/**
 * Phase 11 — Customer invoice mutations.
 *
 * Every mutation calls a server-authoritative edge function. The client
 * never composes status transitions, numbering, signatures, or audit
 * rows directly.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useRecordAdvancedAdvisorAction } from '@/hooks/tickets/useRecordAdvancedAdvisorAction';

function invalidateInvoice(qc: ReturnType<typeof useQueryClient>, invoiceId?: string, ticketId?: string) {
  qc.invalidateQueries({ queryKey: ['customer-invoices'] });
  if (invoiceId) qc.invalidateQueries({ queryKey: ['customer-invoice', invoiceId] });
  if (ticketId) qc.invalidateQueries({ queryKey: ['customer-invoices', 'ticket', ticketId] });
}

/** An edge-function refusal, carrying the server's own reason and code. */
export class InvoiceEdgeError extends Error {
  constructor(message: string, readonly code: string | null) {
    super(message);
    this.name = 'InvoiceEdgeError';
  }
}

export async function invokeInvoiceEdge<T>(name: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    // supabase-js leaves `data` null on a non-2xx response and puts the raw
    // Response on `error.context`. Without reading it, every refusal (an
    // estimate with nothing priced, one still awaiting authorization, work
    // not finished) surfaced as "Edge Function returned a non-2xx status code".
    let payload: any = data;
    if (!payload) {
      try {
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.clone === 'function') payload = await ctx.clone().json();
      } catch { /* non-JSON body: fall back to the transport message */ }
    }
    const message = payload?.error || payload?.message || error.message || 'Edge function error';
    throw new InvoiceEdgeError(String(message), payload?.code ?? null);
  }
  return data as T;
}

export function useCreateCustomerInvoice() {
  const qc = useQueryClient();
  const { activeShop } = useAuth();
  return useMutation({
    mutationFn: async (args: { ticketId: string }) => {
      return invokeInvoiceEdge<{ invoice_id: string; invoice_number: string }>(
        'create-customer-invoice',
        { ticket_id: args.ticketId, shop_id: activeShop?.id },
      );
    },
    onSuccess: (_d, vars) => {
      invalidateInvoice(qc, undefined, vars.ticketId);
      toast({ title: 'Estimate created', description: 'A new estimate has been generated from this ticket.' });
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to create estimate', description: e.message, variant: 'destructive' });
    },
  });
}

export function useRecordAuthorization() {
  const qc = useQueryClient();
  // Slice 5D-6: advisor action emit on successful customer authorization.
  // Wrapped by `useRecordAdvancedAdvisorAction` — gated by
  // `advisorAdvancedActionCapture` + `advisorTimingAnalytics`; no-op
  // when either flag is off. Non-blocking: the hook always resolves,
  // and we fire-and-forget so a logging failure cannot affect the
  // primary mutation's onSuccess path.
  const recordAdvancedAdvisorAction = useRecordAdvancedAdvisorAction();
  return useMutation({
    mutationFn: async (args: {
      invoiceId: string;
      type: 'pre_repair' | 'post_repair';
      customerName: string;
      signaturePng: string;
      authorizationText: string;
      // Slice 5D-6: optional plumbing for advisor action emit. When
      // supplied, an advisor_action_events row of kind
      // `customer_approval_received` is recorded fire-and-forget after
      // the primary mutation succeeds. When missing the emit is silently
      // skipped (no fetch, no error). Existing call sites that don't
      // know the ticket id continue to work unchanged.
      ticketId?: string;
    }) => {
      return invokeInvoiceEdge<{ authorization_id: string }>('record-invoice-authorization', {
        invoice_id: args.invoiceId,
        type: args.type,
        customer_name: args.customerName,
        signature_png: args.signaturePng,
        authorization_text: args.authorizationText,
      });
    },
    onSuccess: (_d, vars) => {
      invalidateInvoice(qc, vars.invoiceId);
      toast({ title: 'Authorization recorded', description: 'Customer signature captured and saved.' });
      // Slice 5D-6: advisor action emit. Metadata holds identifiers and
      // action facts only — never the customer name, signature PNG, or
      // authorization text (PII / content lives in the canonical
      // `customer_invoice_authorizations` table, not in advisor metadata).
      if (vars.ticketId) {
        void recordAdvancedAdvisorAction({
          ticketId: vars.ticketId,
          actionKind: 'customer_approval_received',
          metadata: {
            source_component: 'useRecordAuthorization',
            invoice_id: vars.invoiceId,
            authorization_type: vars.type,
          },
        });
      }
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to record authorization', description: e.message, variant: 'destructive' });
    },
  });
}

export function useFinalizeInvoice() {
  const qc = useQueryClient();
  // Slice 5D-6: advisor action emit on successful invoice finalization.
  // See `useRecordAuthorization` above for the gating + non-blocking
  // contract. The advisor work-product here is "the estimate has been
  // converted to a finalized invoice" — semantically the canonical
  // `invoice_sent` advisor action kind.
  const recordAdvancedAdvisorAction = useRecordAdvancedAdvisorAction();
  return useMutation({
    mutationFn: async (args: { invoiceId: string; ticketId?: string }) => {
      return invokeInvoiceEdge<{ ok: true }>('finalize-customer-invoice', { invoice_id: args.invoiceId });
    },
    onSuccess: (_d, vars) => {
      invalidateInvoice(qc, vars.invoiceId);
      // FIX-R139-LOW-1: previous toast claimed "PDF archived" but the
      // `finalize-customer-invoice` edge function only flips status +
      // timestamps; it does NOT generate or archive a PDF (no DB trigger
      // does either — verified via migration grep). PDF generation
      // requires a separate call to `useGenerateInvoicePdf` →
      // `generate-invoice-pdf`. Removing the false claim closes a
      // UI-vs-backend contract mismatch.
      toast({ title: 'Invoice finalized', description: 'Estimate converted to invoice.' });
      // Slice 5D-6: advisor action emit. Metadata holds identifiers
      // only — no PDF body, no email payload, no customer message.
      if (vars.ticketId) {
        void recordAdvancedAdvisorAction({
          ticketId: vars.ticketId,
          actionKind: 'invoice_sent',
          metadata: {
            source_component: 'useFinalizeInvoice',
            invoice_id: vars.invoiceId,
          },
        });
      }
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to finalize invoice', description: e.message, variant: 'destructive' });
    },
  });
}

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      invoiceId: string;
      kind?: 'payment' | 'refund';
      enteredAmount: number;
      appliedAmount: number;
      changeGiven?: number;
      method: string;
      transactionReference?: string;
      refundOfPaymentId?: string;
      notes?: string;
    }) => {
      return invokeInvoiceEdge<{ payment_id: string }>('record-invoice-payment', {
        invoice_id: args.invoiceId,
        kind: args.kind ?? 'payment',
        entered_amount: args.enteredAmount,
        applied_amount: args.appliedAmount,
        change_given: args.changeGiven ?? 0,
        method: args.method,
        transaction_reference: args.transactionReference,
        refund_of_payment_id: args.refundOfPaymentId,
        notes: args.notes,
      });
    },
    onSuccess: (_d, vars) => {
      invalidateInvoice(qc, vars.invoiceId);
      toast({
        title: vars.kind === 'refund' ? 'Refund recorded' : 'Payment recorded',
        description: vars.kind === 'refund' ? 'Refund has been applied.' : 'Payment has been applied.',
      });
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to record', description: e.message, variant: 'destructive' });
    },
  });
}

export function useVoidInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { invoiceId: string; reason: string }) => {
      return invokeInvoiceEdge<{ ok: true }>('void-customer-invoice', {
        invoice_id: args.invoiceId,
        reason: args.reason,
      });
    },
    onSuccess: (_d, vars) => {
      invalidateInvoice(qc, vars.invoiceId);
      toast({ title: 'Invoice voided' });
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to void', description: e.message, variant: 'destructive' });
    },
  });
}

export function useGenerateInvoicePdf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { invoiceId: string }) => {
      return invokeInvoiceEdge<{ url: string; storage_path: string; content_hash: string }>(
        'generate-invoice-pdf',
        { invoice_id: args.invoiceId },
      );
    },
    onSuccess: (_d, vars) => {
      invalidateInvoice(qc, vars.invoiceId);
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to generate PDF', description: e.message, variant: 'destructive' });
    },
  });
}

export function useUpdateShopInvoicingSettings() {
  const qc = useQueryClient();
  const { activeShop } = useAuth();
  return useMutation({
    mutationFn: async (args: {
      pre_repair_terms?: string;
      post_repair_terms?: string;
      require_pre_auth_before_work?: boolean;
    }) => {
      if (!activeShop?.id) throw new Error('No active shop');
      // Upsert by shop_id
      const { data, error } = await (supabase as any)
        .from('shop_invoicing_settings')
        .upsert({ shop_id: activeShop.id, ...args }, { onConflict: 'shop_id' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shop-invoicing-settings'] });
      toast({ title: 'Invoicing settings saved' });
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to save settings', description: e.message, variant: 'destructive' });
    },
  });
}
